import React, { useState, useRef, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PiHandWaving } from "react-icons/pi";

import { canAccessXoe } from "../../utils/userPrefs/validatePermissions";
import { useAuthSession } from "../../lib/http/session";
import { useXoeChatMode } from "../../utils/userPrefs/UserPrefs";
import { subscribeOpenXoeConversation } from "../../utils/xoe/openXoeConversation";
import { useXoeChatSession } from "../../hooks/xoe/useXoeChatSession";
import XoeChat from "./XoeChat";
import { XOE_PANEL_HEIGHT } from "./xoeLayout";
import type { Edge, WidgetPosition, XoeFloatingChatProps } from "../../types/xoe/XoeUiTypes";

import "./xoe.css";

// ── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = "xoe.widget.position";
const DRAG_THRESHOLD = 6; // px — movement below this = click, not drag
// Tab sizes mirror the .mg-xoe-tab[data-edge] rules in xoe.css; the drag clamp needs them in px.
const TAB_SIZE = 44;
const TAB_LANDSCAPE_WIDTH = 132;
const TAB_PORTRAIT_HEIGHT = 76;

// ── Helpers ──────────────────────────────────────────────────────────────────

function loadPosition(): WidgetPosition {
	const raw = localStorage.getItem(STORAGE_KEY);
	if (raw) {
		const parsed = JSON.parse(raw) as WidgetPosition;
		if (["left", "right", "top", "bottom"].includes(parsed.edge) && typeof parsed.offset === "number") {
			return parsed;
		}
	}

	// First load, nothing saved yet — centre vertically on the right edge.
	return { edge: "right", offset: clampOffset(window.innerHeight / 2, window.innerHeight, tabAlongEdgeExtent("right")) };
}

function savePosition(pos: WidgetPosition): void {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
}

/**
 * The tab's extent along the edge it is parked on — i.e. the axis the drag
 * offset/clamp operate over. For left/right edges the tab is portrait, so
 * the along-edge extent is its height; for top/bottom it is landscape, so
 * the along-edge extent is its width. Must stay in sync with the tab sizes in xoe.css.
 */
function tabAlongEdgeExtent(edge: Edge): number {
	return edge === "left" || edge === "right" ? TAB_PORTRAIT_HEIGHT : TAB_LANDSCAPE_WIDTH;
}

/**
 * Given a pointer (x, y) relative to viewport, determine which edge is nearest
 * and return the offset along that edge.
 */
function snapToNearestEdge(x: number, y: number): WidgetPosition {
	const w = window.innerWidth;
	const h = window.innerHeight;

	const distLeft = x;
	const distRight = w - x;
	const distTop = y;
	const distBottom = h - y;

	const min = Math.min(distLeft, distRight, distTop, distBottom);

	if (min === distLeft) return { edge: "left", offset: clampOffset(y, h, tabAlongEdgeExtent("left")) };
	if (min === distRight) return { edge: "right", offset: clampOffset(y, h, tabAlongEdgeExtent("right")) };
	if (min === distTop) return { edge: "top", offset: clampOffset(x, w, tabAlongEdgeExtent("top")) };
	return { edge: "bottom", offset: clampOffset(x, w, tabAlongEdgeExtent("bottom")) };
}

function clampOffset(value: number, max: number, extent: number): number {
	return Math.max(extent / 2, Math.min(value, max - extent));
}

/**
 * Convert a WidgetPosition into CSS `position: fixed` style props.
 */
function positionToStyle(pos: WidgetPosition): React.CSSProperties {
	const half = tabAlongEdgeExtent(pos.edge) / 2;
	switch (pos.edge) {
		case "left":
			return { left: 0, top: pos.offset - half };
		case "right":
			return { right: 0, top: pos.offset - half };
		case "top":
			return { top: 0, left: pos.offset - half };
		case "bottom":
			return { bottom: 0, left: pos.offset - half };
	}
}

/**
 * Offset the chat panel so it doesn't overlap the tab.
 * Panel appears "inward" from the edge.
 */
function panelOffset(pos: WidgetPosition): React.CSSProperties {
	const gap = TAB_SIZE + 4;
	switch (pos.edge) {
		case "right":
		case "left": {
			const half = TAB_SIZE / 2;
			const containerHeight = TAB_PORTRAIT_HEIGHT;
			const containerBottom = pos.offset - half + containerHeight;
			const sideProp = pos.edge === "right" ? "right" : "left";
			if (containerBottom - XOE_PANEL_HEIGHT < 0) {
				return { [sideProp]: gap, top: 0 };
			}
			return { [sideProp]: gap, bottom: 0 };
		}
		case "top":    return { top: gap, right: 0 };
		case "bottom": return { bottom: gap, right: 0 };
	}
}

// ── Component ────────────────────────────────────────────────────────────────



const XoeWidget: React.FC = () => {
	// NOTE: all hooks must run unconditionally on every render (Rules of Hooks).
	// The auth/role/surface guard early-returns live AFTER every hook call below.
	const signedIn = useAuthSession().status === "authenticated";

	// ── Which Xoe surface(s) this user has chosen ───────────────────────
	const chatMode = useXoeChatMode();
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const onFullPageRoute = pathname === "/xoe" || pathname.startsWith(`/xoe/`);

	// ── State ────────────────────────────────────────────────────────────────
	const [position, setPosition] = useState<WidgetPosition>(loadPosition);
	const [isOpen, setIsOpen] = useState(false);
	const [railOpen, setRailOpen] = useState(false);

	// ── "Discuss with Xoe" handoff ───────────────────────────────
	const [requestedUuid, setRequestedUuid] = useState<string | null>(null);
	const [requestedSeed, setRequestedSeed] = useState<string | null>(null);
	const [requestToken, setRequestToken] = useState(0);
	// Live only when the widget will actually render (see the auth/surface guard below); a signed-out /login tab must fire no Xoe requests.
	const widgetLive = signedIn && canAccessXoe() && chatMode !== "fullpage" && !onFullPageRoute;
	const session = useXoeChatSession({ active: widgetLive, initialUuid: requestedUuid, initialUuidToken: requestToken, initialSeedText: requestedSeed });


	useEffect(() => {
		return subscribeOpenXoeConversation((request) => {
			setRequestedUuid(request.uuid);
			setRequestedSeed(request.seedText ?? null);
			setRequestToken((t) => t + 1);
			setIsOpen(true);
		});
	}, []);

	// Once the widget is closed, drop the requested conversation AND seed so a later
	// manual re-open falls back to the normal "land on most recent" behaviour.
	useEffect(() => {
		if (!isOpen) {
			setRequestedUuid(null);
			setRequestedSeed(null);
			setRailOpen(false);
		}
	}, [isOpen]);

	const handleClose = useCallback(() => setIsOpen(false), []);
	const handleRailCollapsedChange = useCallback((collapsed: boolean) => setRailOpen(!collapsed), []);

	// The page restores the conversation from getLastActiveXoeConversation(); the widget stands down on /xoe.
	const handleMaximise = useCallback(() => {
		setIsOpen(false);
		navigate("/xoe");
	}, [navigate]);

	// ── Drag tracking refs ───────────────────────────────────────────────────
	const dragging = useRef(false);
	const totalMoved = useRef(0);
	const startPointer = useRef({ x: 0, y: 0 });

	// ── Sync position to localStorage whenever it changes ───────────────────
	useEffect(() => {
		savePosition(position);
	}, [position]);

	// ── Pointer handlers ─────────────────────────────────────────────────────
	const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
		e.currentTarget.setPointerCapture(e.pointerId);
		dragging.current = true;
		totalMoved.current = 0;
		startPointer.current = { x: e.clientX, y: e.clientY };
	}, []);

	const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
		if (!dragging.current) return;

		const dx = e.clientX - startPointer.current.x;
		const dy = e.clientY - startPointer.current.y;
		totalMoved.current = Math.sqrt(dx * dx + dy * dy);

		// Live preview while dragging: update position to follow pointer
		if (totalMoved.current >= DRAG_THRESHOLD) {
			const snapped = snapToNearestEdge(e.clientX, e.clientY);
			setPosition(snapped);
		}
	}, []);

	const handlePointerUp = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			if (!dragging.current) return;
			dragging.current = false;

			if (totalMoved.current < DRAG_THRESHOLD) {
				// Treat as a click — toggle chat open/closed
				setIsOpen((prev) => !prev);
			} else {
				// Finalize snap position — the useEffect on `position` persists it
				const snapped = snapToNearestEdge(e.clientX, e.clientY);
				setPosition(snapped);
			}
		},
		[]
	);

	// ── Auth / role guard (AFTER all hooks — Rules of Hooks) ──────────────────
	if (!signedIn || !canAccessXoe()) return null;

	// `fullpage` means the practitioner reaches Xoe from the full-page tab only.
	if (chatMode === "fullpage" || onFullPageRoute) return null;

	const isLandscape = position.edge === "top" || position.edge === "bottom";


	// Only the computed placement is inline; everything static lives in xoe.css.
	return (
		<div className="mg-xoe-widget" style={positionToStyle(position)}>
			<div
				className="mg-xoe-tab"
				data-edge={position.edge}
				data-tucked={String(!isOpen)}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
				aria-label="Open Xoe AI assistant"
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") setIsOpen((prev) => !prev);
				}}
				data-testid="xoe-widget-tab"
			>
				{isLandscape ? (
					<>
						<PiHandWaving size={20} />
						<span className="mg-xoe-tab-label">Ask Xoe</span>
					</>
				) : (
					<>
						<span className="mg-xoe-tab-label">
							Ask<br />Xoe
						</span>
						<PiHandWaving size={20} />
					</>
				)}
			</div>

			{isOpen && (
				<div
					className={railOpen ? "mg-xoe-float-slot mg-xoe-float-slot--rail" : "mg-xoe-float-slot"}
					style={panelOffset(position)}
					data-testid="xoe-chat-panel"
				>
					<XoeChat
						session={session}
						tone="primary"
						className="mg-xoe--float"
						railDefaultCollapsed
						onClose={handleClose}
						closeVariant="minimise"
						onMaximise={handleMaximise}
						onRailCollapsedChange={handleRailCollapsedChange}
					/>
				</div>
			)}
		</div>
	);
};

export default XoeWidget;
